import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Drawer, FormField, Input, SegmentedTabs, toast } from '@/ui';
import { orgTypeCode, type MemberType } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import { LEGAL_COPY } from '@/features/legal/legal-copy';
import { createMember, fetchPermissionCatalogue, type CreatedMember } from './api';
import { PermissionPicker } from './PermissionPicker';
import { organizationKeys } from './query-keys';
import { iamErrorMessage } from './error-message';
import styles from './Organization.module.css';

/**
 * EKIP UYESI EKLEME (mobil InviteMember akisinin web karsiligi).
 *
 * "Davet" degil PROVIZYON: sunucu hesabi hemen acar, gecici bir sifre uretir
 * ve e-postalar. Kullanici ilk giriste sifresini degistirmek ZORUNDA
 * (mustChangePassword).
 *
 * E-POSTA GIDEMEZSE yanit `tempPassword` tasir ve BU TEK SEFERLIKTIR — sunucu
 * bir daha vermez. O yuzden panel kendiliginden kapanmaz, sifre secilebilir
 * halde durur ve yaninda KVKK bilgilendirme metni gosterilir: adi ve
 * e-postasi giren kisi, bilgilendirmeyi de paylasmak zorunda.
 *
 * YONETICI SECILINCE baslangic yetkileri DUSURULUR: yonetici ortuk olarak tam
 * yetkilidir ve sunucu admin + yetki listesi kombinasyonunu
 * ADMIN_PERMISSIONS_IMPLICIT ile reddeder.
 */

const makeInviteSchema = (t: TFunction) =>
  z.object({
    invitedFullName: z
      .string()
      .trim()
      .min(1, { message: t('org.invite.fullNameRequiredDetail') }),
    invitedEmail: z
      .string()
      .trim()
      .min(1, { message: t('validation.emailRequired') })
      .refine((value) => z.email().safeParse(value).success, {
        message: t('org.invite.invalidEmailDetail'),
      }),
  });

type InviteFormValues = z.input<ReturnType<typeof makeInviteSchema>>;

export type InviteMemberDrawerProps = {
  open: boolean;
  onClose: () => void;
};

export function InviteMemberDrawer({ open, onClose }: InviteMemberDrawerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Yetki secici YALNIZ buro org'unda: grant edilebilir kodlar buro ici operasyon yetkileridir.
  // Musteri org'unda personelin ihtiyaci olan viewAccounts'u SUNUCU varsayilan olarak veriyor
  // (iam-service.js / admin-service.js), istemci bos liste gonderir.
  const snapshot = useSessionStore((state) => state.snapshot);
  const isExchangeOrg = orgTypeCode(snapshot) === 'exchange';

  const [memberType, setMemberType] = useState<MemberType>('staff');
  const [initialPermissions, setInitialPermissions] = useState<string[]>([]);
  const [created, setCreated] = useState<CreatedMember | null>(null);
  const [createdEmail, setCreatedEmail] = useState('');

  const schema = useMemo(() => makeInviteSchema(t), [t]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { invitedFullName: '', invitedEmail: '' },
  });

  const catalogue = useQuery({
    queryKey: organizationKeys.permissionCatalogue(),
    queryFn: fetchPermissionCatalogue,
    // Musteri org'unda katalog HIC cekilmiyor: sunucu bu cagrida org baglami istiyor ve
    // sonucu zaten kullanilmayacak.
    enabled: open && isExchangeOrg,
    staleTime: 10 * 60_000,
  });

  const create = useMutation({
    mutationFn: createMember,
    onSuccess: async (result, variables) => {
      setCreated(result);
      setCreatedEmail(variables.invitedEmail);
      reset({ invitedFullName: '', invitedEmail: '' });
      setInitialPermissions([]);
      toast({
        message: result.emailDelivered ? t('org.invite.created') : t('org.invite.createdNoEmail'),
        tone: result.emailDelivered ? 'success' : 'warning',
      });
      // Yeni uye listede YOKSA "ekledim ama gorunmuyor" hatasi olur; ayrica
      // sunucu memberProvisioned denetim kaydi yaziyor.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationKeys.members() }),
        queryClient.invalidateQueries({ queryKey: organizationKeys.me() }),
        queryClient.invalidateQueries({ queryKey: organizationKeys.auditLogRoot() }),
      ]);
    },
    onError: (error: unknown) => {
      toast({ message: iamErrorMessage(error, t), tone: 'danger' });
    },
  });

  const onSubmit = handleSubmit((values) => {
    create.mutate({
      invitedFullName: values.invitedFullName.trim(),
      invitedEmail: values.invitedEmail.trim(),
      memberTypeCode: memberType,
      initialPermissions: memberType === 'admin' || !isExchangeOrg ? [] : initialPermissions,
    });
  });

  const selectMemberType = (next: MemberType): void => {
    setMemberType(next);
    if (next === 'admin') setInitialPermissions([]);
  };

  const toggle = (code: string): void => {
    setInitialPermissions((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code],
    );
  };

  const close = (): void => {
    // Gecici sifre EKRANDAN KAYBOLURSA bir daha alinamaz; panel kapanirken
    // durum temizleniyor cunku bir sonraki acilisin onu gostermesi yanlis olur.
    setCreated(null);
    setCreatedEmail('');
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title={t('org.invite.pageName')}
      footer={
        <>
          <Button tone="ghost" onClick={close}>
            {created ? t('members.done') : t('auth.give')}
          </Button>
          <Button loading={create.isPending} onClick={() => void onSubmit()}>
            {create.isPending ? t('org.invite.sending') : t('members.createAccount')}
          </Button>
        </>
      }
    >
      <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
        <FormField label={t('org.invite.fullName')} error={errors.invitedFullName} required>
          <Input
            placeholder={t('org.invite.fullNamePlaceholder')}
            autoComplete="off"
            {...register('invitedFullName')}
          />
        </FormField>

        <FormField label={t('org.invite.email')} error={errors.invitedEmail} required>
          <Input
            type="email"
            inputMode="email"
            placeholder={t('org.invite.emailPlaceholder')}
            autoComplete="off"
            {...register('invitedEmail')}
          />
        </FormField>

        <div>
          <p className={styles.groupTitle}>{t('org.invite.role')}</p>
          <SegmentedTabs<MemberType>
            label={t('org.invite.role')}
            items={[
              { value: 'staff', label: t('org.invite.roleStaff') },
              { value: 'admin', label: t('org.invite.roleAdmin') },
            ]}
            value={memberType}
            onChange={selectMemberType}
            block
          />
          <p className={styles.muted}>
            {memberType === 'admin' ? t('org.invite.roleAdminHint') : t('org.invite.roleStaffHint')}
          </p>
        </div>

        {memberType === 'staff' && isExchangeOrg ? (
          <div>
            <p className={styles.groupTitle}>{t('org.invite.initialPermissions')}</p>
            <p className={styles.muted}>{t('org.invite.initialPermissionsHint')}</p>
            <PermissionPicker
              catalogue={catalogue.data ?? []}
              selected={initialPermissions}
              onToggle={toggle}
              disabled={create.isPending}
            />
          </div>
        ) : null}

        {/* Yoneticiye, BASKASININ verisini girdigini hatirlatir (KVKK md.10). */}
        <p className={styles.warningNotice}>{LEGAL_COPY.organization.inviteAdminWarning}</p>

        {created && !created.emailDelivered && created.tempPassword ? (
          <div className={styles.warningNotice}>
            <p>{t('org.invite.createdNoEmail')}</p>
            <p>{createdEmail}</p>
            <code className={styles.tempPassword}>{created.tempPassword}</code>
            <p>{LEGAL_COPY.organization.inviteNotice}</p>
          </div>
        ) : null}

        {created?.emailDelivered ? (
          <p className={styles.notice}>{t('org.invite.passwordEmailed')}</p>
        ) : null}
      </form>
    </Drawer>
  );
}
